import type { MessagesPage } from '@/features/atendimento/api';
import { encaixaCardDoInbound } from '@/features/atendimento/card-do-evento';
import { type InboundEvent, aplicaMensagem } from '@/features/atendimento/encaixe-de-mensagem';
import { escreveHeaderDoInbound } from '@/features/atendimento/header-do-evento';
import { debugInvalidate, inboxListeners } from '@/features/atendimento/inbox-debug';
import { getSocket } from '@/features/atendimento/socket';
import { useOpenTicketStore } from '@/features/atendimento/stores/open-ticket-store';
import { useAuthStore } from '@/shared/stores/auth-store';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { playNotificationSound, primeAudioUnlock } from '../lib/play-sound';
import { useNotificationPrefs } from '../store';

interface MessageReceivedPayload {
  tenant_id: number;
  ticket_id: number;
  contact_id: number;
  message_id: number;
  body: string | null;
  type: string;
  ticket_status: 'pending' | 'active' | 'closed';
  ticket_reason: 'created' | 'reused_open';
  /** User atribuído ao ticket — null se pending (sem atendente). */
  user_id?: number | null;
  /** Direção da msg — outbound (resposta nossa de outra janela/atendente/bot)
   *  não deve notificar nem tocar som. */
  direction?: 'inbound' | 'outbound';
  /** Timestamp da msg — inbound é o novo `last_inbound_at` do canal (o
   *  worker emite `received_at`; a api, `created_at`). */
  received_at?: string;
  created_at?: string;
}

/** Pede permissão de notificação desktop (idempotente). */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
}

function bodyPreview(payload: MessageReceivedPayload): string {
  if (payload.body) return payload.body.slice(0, 120);
  switch (payload.type) {
    case 'image':
      return '📷 Imagem';
    case 'audio':
      return '🎤 Áudio';
    case 'video':
      return '🎬 Vídeo';
    case 'document':
      return '📎 Documento';
    case 'sticker':
      return '🌟 Figurinha';
    case 'location':
      return '📍 Localização';
    default:
      return 'Nova mensagem';
  }
}

/**
 * Hook global — escuta `message.received` no Socket.IO e dispara
 * Desktop Notifications + som conforme preferências do user.
 *
 * Decide qual evento (novo ticket vs nova msg, pending vs active) usando
 * `ticket_reason` e `ticket_status` do payload.
 *
 * Não notifica quando aba está focada (atendente já está vendo).
 */
export function useDesktopNotifications(): void {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const currentUserId = useAuthStore((s) => s.user?.id);
  const departmentIds = useAuthStore((s) => s.user?.departmentIds);
  const prefs = useNotificationPrefs();
  const qc = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof Notification === 'undefined') return;

    // Pede permissão preguiçosamente — não bloqueia montagem.
    void requestNotificationPermission();
    // Registra listener pra destravar AudioContext no primeiro click do usuário.
    primeAudioUnlock();

    const socket = getSocket();

    // Função pra entrar nas rooms de notificação. Chamada inicialmente E em cada
    // reconexão (caso WebSocket caia e reconecte).
    const joinRooms = (): void => {
      socket.emit('joinNotifications');
      // biome-ignore lint/suspicious/noConsole: debug de produção ajuda diagnóstico
      console.debug(
        '[notifications] joined rooms (tenant:N:inbox:pending + :active) — connected:',
        socket.connected,
        'id:',
        socket.id,
      );
    };

    if (socket.connected) {
      joinRooms();
    } else {
      // biome-ignore lint/suspicious/noConsole: debug
      console.debug('[notifications] socket NOT connected ainda — esperando connect');
    }
    socket.on('connect', joinRooms);
    const onDisconnect = (reason: string): void => {
      // biome-ignore lint/suspicious/noConsole: debug
      console.debug('[notifications] socket DISCONNECTED — reason:', reason);
    };
    socket.on('disconnect', onDisconnect);

    const handleMessage = (payload: MessageReceivedPayload): void => {
      // biome-ignore lint/suspicious/noConsole: debug
      console.debug('[notifications] message.received', {
        ticket_id: payload.ticket_id,
        ticket_status: payload.ticket_status,
        ticket_reason: payload.ticket_reason,
        body: payload.body,
      });

      // Invalida cache de mensagens do ticket — ESSENCIAL pra cobrir o caso
      // em que AtendimentoPage está unmounted (user em outra rota: Dashboard,
      // /canais, etc). Sem isso, useRealtimeListeners (que fica em
      // AtendimentoPage) não está ativo, msgs chegam e cache de
      // ticket-messages NÃO é invalidado. Quando user volta + clica no card,
      // useTicketMessages retorna cache stale (fresh dentro do staleTime
      // global de 30s) sem refetch → thread mostra msgs antigas até F5.
      //
      // Esse hook é global no AppShell — sempre ativo enquanto autenticado.
      //
      // NÃO invalidamos ['tickets', status] aqui — isso atrapalha o optimistic
      // update do useRealtimeListeners.onInboundInbox que cuida do badge de
      // unread_count. Quando AtendimentoPage está unmounted, o cache de
      // tickets eventualmente vai refetchar quando user voltar (refetchOnMount
      // após o staleTime).
      // Com a AtendimentoPage MONTADA, os listeners dela já cobrem a thread
      // (append na aberta, invalidate nas fechadas) — invalidar aqui de novo
      // só gerava um refetch extra da conversa aberta a CADA mensagem
      // (medido: 2 GET /messages por envio). A defesa fica pro caso que a
      // motivou: user em outra rota, listeners da página desregistrados.
      if (payload.ticket_id && inboxListeners.active === 0) {
        // ENCAIXA a mensagem no cache da conversa com a MESMA máquina dos
        // listeners da AtendimentoPage (aplicaMensagem, extraída pra módulo).
        // Antes só invalidava — abrir a conversa pintava o cache velho e o
        // refetch acrescentava a msg depois: a "montagem" visível (medida no
        // rastro 2026-07-24: `pintou 20 (velho)` + fetch, vs `pintou 21` +
        // zero fetch quando encaixada). Sem cache pra encaixar (conversa
        // nunca aberta), invalida como sempre — o refetch ao abrir cobre.
        let encaixou = false;
        qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
          ['ticket-messages', payload.ticket_id],
          (prev) => {
            if (!prev || prev.pages.length === 0) return prev;
            encaixou = true;
            // O evento no fio é o MESMO `message.received` que os listeners da
            // página consomem — o tipo local declara só os campos usados aqui.
            return aplicaMensagem(prev, payload as unknown as InboundEvent);
          },
        );
        // Remenda o CARD nas listas em cache (preview, badge, subida pro topo)
        // com a mesma máquina da página (encaixaCardDoInbound). Sem isso,
        // voltar pro Atendimento pintava a lista na ordem velha e o refetch
        // reordenava DIANTE do atendente (card "viajando" pro topo — medido no
        // rastro 2026-07-24). openTicketId=null: página desmontada, nenhuma
        // conversa aberta de fato → inbound conta no badge. Card fora das
        // listas carregadas → nada a fazer; o refetch natural de quem voltar
        // (staleTime 5s das listas) traz o cartão do servidor.
        encaixaCardDoInbound(qc, payload as unknown as InboundEvent, null);
        if (!encaixou) {
          void debugInvalidate(
            qc,
            ['ticket-messages', payload.ticket_id],
            'useDesktopNotifications',
            'message.received',
          );
        }
        // Header ['ticket', id] TAMBÉM — status e contact.last_inbound_at
        // (banner 24h + composer bloqueado) mudam com a mensagem e ninguém
        // mais cobre este caminho. ESCREVE do evento (abrir a conversa já vem
        // certo, sem flash do banner) e invalida como rede de segurança: a
        // query está inativa, invalidar só marca stale e o refetch confirma
        // quando o atendente abrir. Medido (rastro 2026-07-24 21:00Z): sem
        // isto, o banner "janela 24h expirou" ficava preso até F5.
        escreveHeaderDoInbound(qc, payload);
        void debugInvalidate(
          qc,
          ['ticket', payload.ticket_id],
          'useDesktopNotifications',
          'message.received',
        );
      }

      // Outbound (resposta de outro atendente OU de outra janela/perfil do mesmo
      // usuário, ou bot) NÃO notifica nem toca som — só msg inbound do contato.
      // O cache de mensagens acima já foi invalidado (thread atualiza); aqui só
      // cortamos o "alarme". Sem isso, sua própria resposta beepava na outra aba.
      if (payload.direction === 'outbound') return;

      // Se o ticket aberto na thread É esse mesmo, o atendente está nessa conversa
      // (mesmo que a aba não esteja focada — o ticket foi aberto intencionalmente).
      // Não polui com som/notif. `isHidden` ainda é usado abaixo só pra Notification visual.
      const openTicketId = useOpenTicketStore.getState().openTicketId;
      const isHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
      const viewingThisTicket = openTicketId === payload.ticket_id;
      if (viewingThisTicket) {
        // biome-ignore lint/suspicious/noConsole: debug
        console.debug(
          `[notifications] ignored — ticket ${payload.ticket_id} já está aberto na thread`,
        );
        return;
      }

      // Filtro de ownership: ticket atribuído a OUTRO user → silencia.
      // - user_id null → pending (qualquer um pode pegar) → toca pra todos
      // - user_id == currentUser → ativo do user logado → toca pra ele
      // - user_id != currentUser → ativo de outro atendente → silencia
      if (payload.user_id != null && currentUserId != null && payload.user_id !== currentUserId) {
        // biome-ignore lint/suspicious/noConsole: debug
        console.debug(
          `[notifications] silenced — ticket ${payload.ticket_id} é de outro user (${payload.user_id})`,
        );
        return;
      }

      const isNewTicket = payload.ticket_reason === 'created';
      const isPending = payload.ticket_status === 'pending';
      const isActive = payload.ticket_status === 'active';

      let allowed = false;
      let title = '';
      if (isNewTicket && isPending && prefs.newTicketPending) {
        allowed = true;
        title = 'Novo ticket pendente';
      } else if (isNewTicket && isActive && prefs.newTicketActive) {
        allowed = true;
        title = 'Novo ticket ativo';
      } else if (!isNewTicket && isActive && prefs.newMessageActive) {
        allowed = true;
        title = 'Nova mensagem (em atendimento)';
      } else if (!isNewTicket && isPending && prefs.newMessagePending) {
        allowed = true;
        title = 'Nova mensagem pendente';
      }

      if (!allowed) {
        // biome-ignore lint/suspicious/noConsole: debug
        console.debug('[notifications] ignored — pref desabilitada pra esse cenário');
        return;
      }

      // Som sempre toca (mesmo aba ativa) — discreto e ajuda atendente que está em outro ticket.
      if (prefs.soundEnabled) {
        playNotificationSound();
      }

      // Notification OS só dispara quando aba NÃO está visível (Chrome silencia visualmente
      // com aba ativa de qualquer jeito — evita ruído).
      if (isHidden && Notification.permission === 'granted') {
        const n = new Notification(title, {
          body: bodyPreview(payload),
          tag: `ticket-${payload.ticket_id}`,
        });
        n.onclick = () => {
          window.focus();
          window.location.hash = `#/atendimento?ticket=${payload.ticket_id}`;
          n.close();
        };
      }
    };

    socket.on('message.received', handleMessage);

    // ticket.transferred / ticket.queued → toca som quando um ticket fica
    // disponível pra mim. Cenários:
    //
    // 1. Transferido especificamente PRA MIM (to_user_id == me) — alguém me
    //    mandou um ticket; toca som SEMPRE (exceto se eu mesmo transferi).
    // 2. Transferido pra DEPARTAMENTO que eu pertenço (to_user_id null e
    //    to_department_id ∈ minhas depts) — fila do meu dept ganhou ticket;
    //    qualquer um pode pegar, toca pra todos os dept members.
    // 3. Ticket voltou pra FILA (ticket.queued) — algum atendente liberou,
    //    qualquer um do meu dept pode pegar; toca pra todos exceto o actor.
    //
    // Dedup: a API emite ticket.transferred pra DEPT ANTIGO e NOVO. Se eu
    // pertenço aos dois, recebo 2x. `recentlyNotified` ignora repetição em
    // janela de 2s por chave (ticket_id + actor).
    const recentlyNotified = new Set<string>();
    const dedup = (key: string): boolean => {
      if (recentlyNotified.has(key)) return true;
      recentlyNotified.add(key);
      setTimeout(() => recentlyNotified.delete(key), 2000);
      return false;
    };

    const playTransferSound = (ticketId: number | undefined, title: string, body: string): void => {
      if (prefs.soundEnabled) {
        playNotificationSound();
      }
      const isHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
      if (isHidden && Notification.permission === 'granted' && ticketId) {
        const n = new Notification(title, { body, tag: `ticket-transfer-${ticketId}` });
        n.onclick = () => {
          window.focus();
          window.location.hash = `#/atendimento?ticket=${ticketId}`;
          n.close();
        };
      }
    };

    const handleTransferred = (payload: {
      ticket_id?: number;
      to_user_id?: number | null;
      to_department_id?: number | null;
      actor_user_id?: number | null;
    }): void => {
      if (currentUserId == null) return;
      if (payload.actor_user_id === currentUserId) return; // eu mesmo transferi

      const key = `transfer-${payload.ticket_id}-${payload.actor_user_id ?? 'sys'}`;
      if (dedup(key)) return;

      // Caso 1: transferido pra mim diretamente
      if (payload.to_user_id === currentUserId) {
        console.debug(`[notifications] ticket.transferred → me, ticket ${payload.ticket_id}`);
        playTransferSound(
          payload.ticket_id,
          'Ticket transferido pra você',
          'Você recebeu um novo atendimento.',
        );
        return;
      }

      // Caso 2: transferido pro meu departamento (sem user específico)
      if (payload.to_user_id == null && payload.to_department_id != null) {
        const myDepts = departmentIds ?? [];
        if (myDepts.includes(payload.to_department_id)) {
          console.debug(
            `[notifications] ticket.transferred → minha dept (${payload.to_department_id}), ticket ${payload.ticket_id}`,
          );
          playTransferSound(
            payload.ticket_id,
            'Novo ticket na fila',
            'Um atendimento foi transferido para a sua fila.',
          );
        }
      }
    };
    socket.on('ticket.transferred', handleTransferred);

    // ticket.queued: atendente devolveu pra fila — toca pra todos do dept
    // (eles já estão no dept room, então auto-filtragem por room cobre).
    const handleQueued = (payload: {
      ticket_id?: number;
      actor_user_id?: number | null;
    }): void => {
      if (currentUserId == null) return;
      if (payload.actor_user_id === currentUserId) return; // eu mesmo devolvi
      const key = `queue-${payload.ticket_id}-${payload.actor_user_id ?? 'sys'}`;
      if (dedup(key)) return;
      console.debug(
        `[notifications] ticket.queued, ticket ${payload.ticket_id}, actor ${payload.actor_user_id}`,
      );
      playTransferSound(
        payload.ticket_id,
        'Ticket voltou pra fila',
        'Um atendimento ficou disponível na sua fila.',
      );
    };
    socket.on('ticket.queued', handleQueued);

    // ticket.created SEM mensagem (ex: ligação entrante cria o pendente no
    // toque — worker-zapo/voip). Não há message.received pra materializar o
    // card, então invalida a lista de pendentes (lição #59: invalidate marca
    // stale e o refetch pinta o card em quem está com a fila aberta).
    const handleTicketCreated = (payload: { ticket_id?: number }): void => {
      console.debug(`[notifications] ticket.created, ticket ${payload.ticket_id}`);
      // Atendimento novo com mensagem: o `message.received`, que chega antes,
      // já disparou a busca de Pendentes. `cancelRefetch: false` aproveita essa
      // busca em andamento em vez de cancelar e repetir — eram 2 buscas da
      // mesma lista por atendimento novo (medido no srv03, 03/nov). Sem busca
      // em andamento (ligação entrante, mídia), busca como sempre.
      void qc.invalidateQueries({ queryKey: ['tickets', 'pending'] }, { cancelRefetch: false });
    };
    socket.on('ticket.created', handleTicketCreated);

    return () => {
      socket.off('message.received', handleMessage);
      socket.off('ticket.transferred', handleTransferred);
      socket.off('ticket.queued', handleQueued);
      socket.off('ticket.created', handleTicketCreated);
      socket.off('connect', joinRooms);
      socket.off('disconnect', onDisconnect);
    };
  }, [
    isAuthenticated,
    currentUserId,
    departmentIds,
    prefs.newTicketPending,
    prefs.newTicketActive,
    prefs.newMessageActive,
    prefs.newMessagePending,
    prefs.soundEnabled,
  ]);
}
